import { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { RiDashboardLine } from "react-icons/ri";
import { FiMail, FiLock, FiAlertCircle, FiEye, FiEyeOff, FiRefreshCw, FiUser, FiBriefcase } from "react-icons/fi";
import { useLanguage } from "../contexts/LanguageContext";
import { useDarkMode } from "../hooks/useDarkMode";
import { useSystemSettings } from "../hooks/usesystemsettings";

const LoginPage = () => {
  const { t, toggleLang, lang, isRTL } = useLanguage();
  const { darkMode } = useDarkMode();
  // When the system admin disables public signup we hide the Register
  // tab + the "create an account" link entirely. The tab can be left
  // visible too, but per spec the signup path should be invisible.
  const { settings: sysSettings } = useSystemSettings();
  const signupEnabled = sysSettings.signupEnabled !== false;
  const [activeTab, setActiveTab] = useState("login");
  const [formData, setFormData] = useState({ email: "", password: "", name: "", companyName: "", businessType: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      // Navigate immediately. AuthContext's onAuthStateChanged listener
      // will set loading=true and ProtectedRoute will show the spinner
      // until userData/tenant/features are fetched, so there is no
      // flash of empty state. The previous 500ms setTimeout was a
      // band-aid that just made the login button hang.
      navigate("/company");
    } catch (err) {
      const map = { "auth/invalid-email": t("login_invalid"), "auth/user-not-found": t("login_not_found"), "auth/wrong-password": t("login_invalid"), "auth/invalid-credential": t("login_invalid") };
      setError(map[err.code] || t("login_failed"));
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    if (!formData.businessType) { setError(t("select_business_type")); setLoading(false); return; }
    if (!formData.companyName) { setError(t("company_name")); setLoading(false); return; }
    try {
      const createTenantFn = httpsCallable(functions, "createTenant");
      await createTenantFn({ ownerEmail: formData.email, ownerPassword: formData.password, ownerName: formData.name || formData.email.split("@")[0], companyName: formData.companyName, businessType: formData.businessType });
      // Sign in immediately after createTenant succeeds. AuthContext
      // handles the user/tenant/features fetch and ProtectedRoute waits
      // for it — no need for an arbitrary timeout here.
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      navigate("/company", { replace: true });
    } catch (err) {
      if (err.code === "auth/email-already-in-use" || err.message?.includes("409")) { setError(t("login_invalid")); setActiveTab("login"); }
      else setError(err.message || t("register_failed"));
    } finally { setLoading(false); }
  };

  const businessTypes = [
    { value: "agency", label: t("business_type_agency"), hint: t("business_type_hint_agency") },
    { value: "store", label: t("business_type_store"), hint: t("business_type_hint_store") },
    { value: "service", label: t("business_type_service"), hint: t("business_type_hint_service") },
  ];

  return (
    <div  className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black" : "bg-gradient-to-br from-blue-50 via-white to-gray-100"} flex items-center justify-center p-4`}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className={`${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200 shadow-xl"} backdrop-blur-sm border rounded-2xl p-8 w-full max-w-md`}>

        {/* Lang toggle */}
        <div className={`flex justify-end mb-4`}>
          <button onClick={toggleLang} className={`text-xs ${darkMode ? "text-gray-400 hover:text-white border-gray-600" : "text-gray-500 hover:text-gray-900 border-gray-300"} border px-3 py-1 rounded-full transition-colors`}>
            {lang === "en" ? "عربي" : "EN"}
          </button>
        </div>

        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <RiDashboardLine className="text-4xl text-blue-400" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">NexusFlow</h1>
        </div>

        {/* Tabs — when public signup is disabled we drop the
            register tab entirely. Showing a disabled register tab
            would be a clear "you can't sign up" signal which the
            spec explicitly avoids — the page should look like a
            simple login screen. */}
        <div className={`flex gap-2 mb-6 ${darkMode ? "bg-gray-700/50" : "bg-gray-100"} rounded-xl p-1 `}>
          {(signupEnabled ? ["login", "register"] : ["login"]).map(tab => (
            <button key={tab} type="button" onClick={() => { setActiveTab(tab); setError(""); }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${activeTab === tab ? "bg-blue-600 text-white" : (darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900")}`}>
              {tab === "login" ? t("sign_in") : t("register")}
            </button>
          ))}
        </div>

        <h2 className={`text-xl ${darkMode ? "text-white" : "text-gray-900"} text-center mb-6`}>
          {activeTab === "login" ? t("sign_in_to_account") : t("create_company_account")}
        </h2>

        {error && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl mb-4 flex items-center gap-2">
            <FiAlertCircle className="flex-shrink-0" /><span className="text-sm">{error}</span>
          </motion.div>
        )}

        <form onSubmit={activeTab === "login" ? handleLogin : handleRegister} className="space-y-4">
          {activeTab === "register" && (
            <>
              {/* Company Name */}
              <div>
                <label className={`${darkMode ? "text-gray-400" : "text-gray-600"} text-sm mb-1 block`}>{t("company_name")} <span className="text-red-400">*</span></label>
                <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange}
                  className={`w-full ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-400"} border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                  placeholder={t("company_name")} required disabled={loading} />
              </div>

              {/* Business Type */}
              <div>
                <label className={`${darkMode ? "text-gray-400" : "text-gray-600"} text-sm mb-1 block`}>{t("business_type")} <span className="text-red-400">*</span></label>
                <div className="relative">
                  <FiBriefcase className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? "text-gray-400" : "text-gray-500"} pointer-events-none`} />
                  <select name="businessType" value={formData.businessType} onChange={handleInputChange}
                    className={`w-full ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-gray-100 border-gray-300 text-gray-900"} border rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none cursor-pointer`}
                    required disabled={loading}>
                    <option value="">{t("select_business_type")}</option>
                    {businessTypes.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
                  </select>
                </div>
                {formData.businessType && (
                  <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-1 `}>
                    {businessTypes.find(b => b.value === formData.businessType)?.hint}
                  </p>
                )}
              </div>

              {/* Name */}
              <div>
                <label className={`${darkMode ? "text-gray-400" : "text-gray-600"} text-sm mb-1 block`}>{t("your_name")}</label>
                <div className="relative">
                  <FiUser className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                    className={`w-full ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-400"} border rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                    placeholder={t("your_name")} disabled={loading} />
                </div>
              </div>
            </>
          )}

          {/* Email */}
          <div>
            <label className={`${darkMode ? "text-gray-400" : "text-gray-600"} text-sm mb-1 block`}>{t("email")} <span className="text-red-400">*</span></label>
            <div className="relative">
              <FiMail className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
              <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                className={`w-full ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-400"} border rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                placeholder="your@email.com" required disabled={loading} />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className={`${darkMode ? "text-gray-400" : "text-gray-600"} text-sm mb-1 block`}>{t("password")} <span className="text-red-400">*</span></label>
            <div className="relative">
              <FiLock className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
              <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleInputChange}
                className={`w-full ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-400"} border rounded-xl pl-10 pr-12 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                placeholder="••••••••" required disabled={loading} minLength={6} />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${darkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-500 hover:text-gray-700"}`}>
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {activeTab === "register" && <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-1 `}>{t("min_password")}</p>}
          </div>

          <button type="submit" disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 disabled:cursor-not-allowed text-white font-medium py-3 rounded-xl transition-colors flex items-center justify-center gap-2 mt-6">
            {loading ? (
              <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}><FiRefreshCw /></motion.div>
                <span>{activeTab === "login" ? t("signing_in") : t("creating_account")}</span></>
            ) : (activeTab === "login" ? t("sign_in") : t("register"))}
          </button>
        </form>

        <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-center mt-6 text-sm`}>
          {activeTab === "login" ? (
            // Only show the "no account yet → register" line when
            // public signup is enabled. Otherwise the login screen
            // ends cleanly without a dangling link.
            signupEnabled ? (
              <>{t("no_account_yet")}{" "}
                <button type="button" onClick={() => { setActiveTab("register"); setError(""); }} className="text-blue-400 hover:text-blue-300 hover:underline">{t("register_company")}</button></>
            ) : null
          ) : (
            <>{t("already_have_account")}{" "}
              <button type="button" onClick={() => { setActiveTab("login"); setError(""); }} className="text-blue-400 hover:text-blue-300 hover:underline">{t("sign_in")}</button></>
          )}
        </p>
      </motion.div>
    </div>
  );
};

export default LoginPage;